// What a studio resource is as media, read from its URL. The admin-chosen
// `type` ("Video", "Training Materials") is a free-form group name, not a
// format: three "Prospect Explainer" recordings filed under Training Materials
// are MP3s, and Sabine listed them as videos (SAL-3285). Anything that asks
// "does this play, and as what?" asks here.

import {
  getYouTubeId,
  isDirectVideoUrl,
  isVideoResource,
} from "@/lib/resources/videoThumbnail";

const AUDIO_FILE_RE = /\.(mp3|wav|m4a|aac|flac|ogg|oga)(?:[?#]|$)/i;
const PDF_FILE_RE = /\.pdf(?:[?#]|$)/i;
const OFFICE_FILE_RE = /\.(docx?|pptx?|xlsx?)(?:[?#]|$)/i;
const PRESENTATION_FILE_RE = /\.pptx?(?:[?#]|$)/i;
const WORD_FILE_RE = /\.docx?(?:[?#]|$)/i;
const IMAGE_FILE_RE = /\.(jpe?g|gif|png|webp)(?:[?#]|$)/i;

export const RESOURCE_MEDIA_KIND_LABELS = {
  video: "Video",
  audio: "Audio",
  text: "Written lesson",
  pdf: "PDF",
  document: "Document",
  image: "Image",
  link: "Link",
};

export function isAudioUrl(url = "") {
  return AUDIO_FILE_RE.test(String(url || ""));
}

/** A Word file, wherever it is hosted. */
export function isWordDocumentUrl(url = "") {
  return WORD_FILE_RE.test(String(url || ""));
}

/** A slide deck file, whose text no dependency here can read (SAL-3321). */
export function isPresentationUrl(url = "") {
  return PRESENTATION_FILE_RE.test(String(url || ""));
}

/**
 * The body an author wrote in the app, or "". A resource with a url is the
 * file or link itself: since SAL-3321 its `textContent` may hold text
 * extracted from that file for search and Sabine, which is never what to
 * render in its place. Every "is this a written lesson?" branch asks here.
 */
export function getWrittenContent(resource) {
  if (!resource?.textContent) return "";
  // Older lessons stored a placeholder ("#text-content") beside their text.
  const hasFile = /^https?:\/\//i.test(String(resource.url || "").trim()) || resource.isEmbed;
  return hasFile ? "" : resource.textContent;
}

/** One of the RESOURCE_MEDIA_KIND_LABELS keys. */
export function getResourceMediaKind(resource) {
  const url = resource?.url || "";
  if (getWrittenContent(resource)) return "text";
  if (isVideoResource(resource)) return "video";
  if (isAudioUrl(url)) return "audio";
  if (PDF_FILE_RE.test(url)) return "pdf";
  if (OFFICE_FILE_RE.test(url)) return "document";
  if (IMAGE_FILE_RE.test(url)) return "image";
  return "link";
}

/**
 * How a resource plays in place, or null when it is not media: an allowlisted
 * embed, a YouTube video, a video file or an audio file. `ResourcePlayer`
 * renders exactly these four.
 */
export function getInlinePlayback(resource) {
  const url = resource?.url || "";
  if (!url) return null;
  if (resource.isEmbed) return "embed";
  if (getYouTubeId(url)) return "youtube";
  if (isDirectVideoUrl(url)) return "video";
  if (isAudioUrl(url)) return "audio";
  return null;
}

/** A video or recording someone can play without leaving the page. */
export function isPlayableMedia(resource) {
  const kind = getResourceMediaKind(resource);
  return (kind === "video" || kind === "audio") && Boolean(getInlinePlayback(resource));
}
